import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { UserContextV2 } from '~/app';
import { LoadingSpinner } from '~/components';
import { Notification } from '~/types/common/notification';
import { Notification as NotificationComponent } from './Notification';
import { NOTIFICATION_TOGGLE_SELECTOR } from './constants';
import { NotificationGroup, groupNotifications } from './groupNotifications';

const TAGS = CT.notifications;

// These are module-level components (not defined inside NotificationsInner)
// so their identity is stable across renders. Defining them inline caused
// React to unmount/remount the whole list on every state change, resetting
// the scroll position of the overflow container and swallowing clicks on
// notifications below the fold (MCV-1467).

const NotificationCount = ({ loading, count }: { loading: boolean; count: number }) =>
  loading ? (
    <LoadingSpinner size={10} />
  ) : (
    <span
      className="inline-flex items-center rounded-full bg-primary-t-900 px-2.5 py-0.5 text-xs font-semibold text-primary-50"
      data-cy={TAGS.count}
    >
      {count}
    </span>
  );

// [MCV-1918] Alerts about one listing share a row. It shows the latest alert
// and a toggle for the older ones, which keep their own dismiss buttons. The
// latest alert's X dismisses the whole stack, so the stack never leaves a
// stale older alert behind once its newest one is gone.
const NotificationGroupRow = ({
  group,
  index,
  onDismiss,
  onDismissMany,
  onNavigate,
}: {
  group: NotificationGroup;
  index: number;
  onDismiss: (id: number) => void;
  onDismissMany: (ids: number[]) => void;
  onNavigate: (id: number) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const olderId = useId();
  const [latest, ...older] = group.items;
  const stacked = older.length > 0;

  return (
    <div>
      <NotificationComponent
        {...latest}
        cyTag={`${TAGS.num}${index}`}
        handleClick={stacked ? () => onDismissMany(group.items.map((item) => item.id)) : onDismiss}
        dismissLabel={
          stacked ? `Dismiss ${group.items.length} alerts about this listing` : undefined
        }
        onNavigate={onNavigate}
      />
      {stacked && (
        <>
          <div className="mx-3 -mt-1 mb-3">
            <button
              type="button"
              className="text-xs font-semibold text-primary-50 hover:underline cursor-pointer"
              aria-expanded={expanded}
              aria-controls={olderId}
              data-cy={TAGS.groupToggle}
              onClick={() => setExpanded((open) => !open)}
            >
              {`+${older.length} more about this listing`}
            </button>
          </div>
          <div id={olderId} hidden={!expanded}>
            {expanded &&
              older.map((notification, olderIdx) => (
                <NotificationComponent
                  key={notification.id}
                  {...notification}
                  cyTag={`${TAGS.num}${index}-${olderIdx + 1}`}
                  handleClick={onDismiss}
                  onNavigate={onNavigate}
                />
              ))}
          </div>
        </>
      )}
    </div>
  );
};

const NotificationContent = ({
  loading,
  notifications,
  onDismiss,
  onDismissMany,
  onNavigate,
}: {
  loading: boolean;
  notifications: Notification[];
  onDismiss: (id: number) => void;
  onDismissMany: (ids: number[]) => void;
  onNavigate: (id: number) => void;
}) => {
  const groups = useMemo(() => groupNotifications(notifications), [notifications]);

  return (
    <div className="p-2 items-center gap-2 min-w-0 max-h-[calc(100vh-200px-var(--impersonation-banner-height,0px))] overflow-y-auto">
      {loading && <LoadingSpinner />}
      {notifications.length === 0 && !loading && (
        <div className="flex items-center gap-2 justify-center px-2 py-4">
          <CheckCircleIcon className="size-5 text-good" />
          <p className="text-grey-400 italic text-sm text-center min-w-0">You are all caught up!</p>
        </div>
      )}
      {groups.map((group, idx) => (
        <NotificationGroupRow
          key={group.key}
          group={group}
          index={idx}
          onDismiss={onDismiss}
          onDismissMany={onDismissMany}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
};

export const Notifications = () => {
  const userRef = UserContextV2.useActorRef();
  const { notificationsRef } = useSelector(userRef, (s) => s.context);

  if (!notificationsRef) return null;

  return <NotificationsInner />;
};

const NotificationsInner = () => {
  const userRef = UserContextV2.useActorRef();
  const { notificationsRef } = useSelector(userRef, (s) => s.context);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- parent Notifications guards null before rendering this component
  const { loading, isOpen, notifications } = useSelector(notificationsRef!, (s) => s.context);

  // The panel is a fixed popover at every breakpoint. Keeping it out of the
  // Layout flow prevents it from permanently consuming the top of a narrow
  // viewport (MCV-1636), while the same click-away behavior works for both
  // header variants. It hangs 10px under the 72px top bar, which starts under
  // the impersonation banner when one is shown [MCV-1752].
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      const targetElement = target instanceof Element ? target : target.parentElement;
      if (targetElement?.closest(NOTIFICATION_TOGGLE_SELECTOR)) {
        return;
      }
      notificationsRef!.send({ type: 'ON_TOGGLE_PANEL' });
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') notificationsRef!.send({ type: 'ON_TOGGLE_PANEL' });
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, notificationsRef]);

  const handleNotificationDismiss = (id: number) => {
    notificationsRef?.send({ type: 'ON_READ', payload: { id } });
  };

  const handleNotificationDismissMany = (ids: number[]) => {
    notificationsRef?.send({ type: 'ON_READ_MANY', payload: { ids } });
  };

  const handleNotificationNavigate = () => {
    // Notifications lives outside the routed page content, so it remains
    // mounted after a notification link changes routes. Close the fixed
    // popover before that navigation paints over the destination.
    notificationsRef?.send({ type: 'ON_TOGGLE_PANEL' });
  };

  return (
    <div>
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed top-[calc(var(--impersonation-banner-height,0px)+82px)] right-4 left-4 z-50 sm:left-auto sm:w-80 min-[1360px]:right-6"
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-grey-900 pb-3 min-w-0 overflow-hidden"
            data-cy={TAGS.layout}
          >
            <div className="pt-3 mx-3">
              <div className="flex flex-row justify-between items-center gap-4 pb-3 border-b border-grey-900">
                <h1 className="text-[15px] font-semibold text-grey-100 text-left truncate min-w-0">
                  Alerts
                </h1>
                <div className="flex items-center gap-2">
                  <NotificationCount loading={loading} count={notifications.length} />
                  <button
                    onClick={() => notificationsRef?.send({ type: 'ON_TOGGLE_PANEL' })}
                    className="rounded-full p-1 text-grey-400 hover:text-grey-100 hover:bg-grey-900 transition-colors cursor-pointer"
                    aria-label="Hide alerts"
                  >
                    <XMarkIcon className="size-4" />
                  </button>
                </div>
              </div>
            </div>
            <NotificationContent
              loading={loading}
              notifications={notifications}
              onDismiss={handleNotificationDismiss}
              onDismissMany={handleNotificationDismissMany}
              onNavigate={handleNotificationNavigate}
            />
            {notifications.length > 0 && (
              <div className="mx-3 flex justify-end border-t border-grey-900 pt-3">
                <button
                  type="button"
                  className="text-sm font-semibold text-primary-50 hover:underline cursor-pointer"
                  data-cy={TAGS.markAllRead}
                  onClick={() => notificationsRef?.send({ type: 'ON_READ_ALL' })}
                >
                  Mark all as read
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
